import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef, ReactNode } from 'react'
import { userService } from '../services/userService'
import { AuthUser, PermissionInput, PermissionString, UserCapabilities, FeatureKey, EnabledFeatures } from '../types/auth'
import { ensurePageStateOwner, pageStateIdentity } from '../utils/pageStateOwner'
import { buildApiUrl } from '../config/api'
import { clearReturnTo } from '../utils/returnTo'
import { GlobalTipoCode, CapKey, checkRank, checkCap } from '../lib/authz'

/** Inatividade máxima antes do logout automático (exigência de sessão — 15 min). */
const IDLE_TIMEOUT_MS = 15 * 60 * 1000

const EMPTY_CAPABILITIES: UserCapabilities = {
  canViewEmployees: false,
  canManageEmployees: false,
  canEditEmployeeRole: false,
  canEditEmployeeManager: false,
  canManageAutomations: false,
  canManageProcesses: false,
  canManageSolicitacoes: false,
  canApproveSolicitacoes: false,
  canExecuteAutomations: false,
  canViewPowerBI: false,
  canExportData: false,
  canManageDevTools: false,
  canManageJobControl: false,
  canViewFeedbackDashboard: false,
  canViewCostCenterQueue: false,
  canAccessPlanner: false,
  canAccessOccurrences: false,
  canManageExternalUsers: false,
}

const EMPTY_PERMISSIONS: PermissionString[] = []

interface AuthContextType {
  user: AuthUser | null
  token: string | null
  login: (token: string, user: AuthUser) => Promise<void>
  loginWithToken: (token: string) => Promise<void>
  logout: () => void
  isAuthenticated: boolean
  isLoading: boolean
  refreshUser: () => Promise<void>
  permissions: PermissionString[]
  capabilities: UserCapabilities
  /** @deprecated use hasRank/hasCap (Plano D) */
  /** Modelo unificado (Plano B/D). Fail-closed: sem rank/role → false. */
  hasRank: (min: number | GlobalTipoCode) => boolean
  /** Modelo unificado (Plano B/D). Fail-closed: sem caps → false. */
  hasCap: (key: CapKey | CapKey[], mode?: 'all' | 'any') => boolean
  can: (permission: PermissionInput) => boolean
  canAccess: (permissions: PermissionInput | PermissionInput[], options?: { mode?: 'all' | 'any' }) => boolean
  isFeatureEnabled: (key: FeatureKey) => boolean
  enabledFeatures: EnabledFeatures
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Verificar se há dados salvos no localStorage
    const savedToken = localStorage.getItem('token')
    const savedUser = localStorage.getItem('user')

    if (savedToken && savedUser && savedUser !== 'null' && savedUser !== 'undefined') {
      try {
        const parsedUser: AuthUser = JSON.parse(savedUser)
        // Purga o estado de página de outro usuário ANTES do setUser: os hooks
        // do painel/spreadsheet hidratam o localStorage em inicializadores de
        // useState, que rodam no render disparado por este setUser.
        ensurePageStateOwner(pageStateIdentity(parsedUser))
        setToken(savedToken)
        setUser(parsedUser)

        // Buscar perfil completo do usuário após carregar dados básicos
        refreshUserProfile()
      } catch (error) {
        console.error('Erro ao fazer parse dos dados do usuário:', error)
        // Limpar dados inválidos
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        setIsLoading(false)
      }
    } else {
      setIsLoading(false)
    }
  }, [])

  const refreshUserProfile = async () => {
    try {
      const profile = await userService.getProfile()
      // Identidade efetiva pode mudar sem novo login (ex.: modo "ver como
      // externo"); purga o estado de página antes de refletir o perfil.
      ensurePageStateOwner(pageStateIdentity(profile))
      setUser(profile)
      localStorage.setItem('user', JSON.stringify(profile))
    } catch (error) {
      console.error('Erro ao buscar perfil do usuário:', error)
      // Se falhar, manter dados do localStorage
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (newToken: string, newUser: AuthUser) => {
    if (!newToken || !newUser) {
      console.error('Token ou usuário inválidos no login')
      return
    }

    ensurePageStateOwner(pageStateIdentity(newUser))
    setToken(newToken)
    setUser(newUser)
    localStorage.setItem('token', newToken)
    localStorage.setItem('user', JSON.stringify(newUser))
    
    // Buscar perfil completo após login
    await refreshUserProfile()
  }

  const loginWithToken = async (newToken: string) => {
    if (!newToken) {
      return
    }

    setToken(newToken)
    localStorage.setItem('token', newToken)
    
    // Buscar perfil completo após login
    await refreshUserProfile()
  }

  const refreshUser = async () => {
    await refreshUserProfile()
  }

  const logout = useCallback(() => {
    // Revoga a sessão no servidor (best-effort). Raw fetch (não o apiClient) para
    // não disparar o interceptor de 401 → logout recursivo. Mesmo se falhar,
    // limpamos o estado local.
    const current = localStorage.getItem('token')
    if (current) {
      void fetch(buildApiUrl('/auth/logout'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${current}` },
      }).catch(() => {})
    }
    setToken(null)
    setUser(null)
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    // Logout explícito encerra a intenção de navegação: destino salvo não
    // deve valer pro próximo login na mesma aba (terminal compartilhado).
    clearReturnTo()
  }, [])

  // Ruling DD: aba já montada antes do rollout do Plano B guarda um usuário
  // interno sem `caps` em memória — só reidrata em mount/login/refreshUser
  // manual. Auto-heal: 1 tentativa de refreshUserProfile() por sessão de
  // componente (ref evita loop se o backend ainda não devolver caps).
  const capsHealAttempted = useRef(false)
  useEffect(() => {
    if (!user || user.caps != null) return
    if (capsHealAttempted.current) return
    capsHealAttempted.current = true
    refreshUserProfile()
  }, [user])

  const isAuthenticated = !!token && !!user

  // Logout automático por inatividade (15 min sem interação do usuário). O
  // backend também impõe o idle via TTL da sessão; este é o reforço "de tela".
  useEffect(() => {
    if (!isAuthenticated) return
    let timer: ReturnType<typeof setTimeout>
    const reset = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        logout()
        window.location.href = '/login?motivo=inatividade'
      }, IDLE_TIMEOUT_MS)
    }
    const eventos: (keyof WindowEventMap)[] = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ]
    eventos.forEach((e) => window.addEventListener(e, reset, { passive: true }))
    reset()
    return () => {
      clearTimeout(timer)
      eventos.forEach((e) => window.removeEventListener(e, reset))
    }
  }, [isAuthenticated, logout])

  const permissions = useMemo<PermissionString[]>(() => {
    return user?.permissions?.permissions ?? EMPTY_PERMISSIONS
  }, [user?.permissions?.permissions])

  const permissionSet = useMemo(() => new Set<PermissionString>(permissions), [permissions])

  const capabilities = useMemo<UserCapabilities>(() => {
    if (user?.permissions?.capabilities) {
      return { ...user.permissions.capabilities }
    }
    return { ...EMPTY_CAPABILITIES }
  }, [user?.permissions?.capabilities])

  const hasRank = useCallback(
    (min: number | GlobalTipoCode) => checkRank(user, min),
    [user],
  )

  const hasCap = useCallback(
    (key: CapKey | CapKey[], mode: 'all' | 'any' = 'all') => checkCap(user, key, mode),
    [user],
  )

  const toPermissionString = useCallback((permission: PermissionInput): PermissionString => {
    if (typeof permission === 'string') {
      return permission as PermissionString
    }
    return `${permission.action}:${permission.subject}` as PermissionString
  }, [])

  const can = useCallback(
    (permission: PermissionInput) => {
      if (permissionSet.size === 0) return false
      return permissionSet.has(toPermissionString(permission))
    },
    [permissionSet, toPermissionString],
  )

  const canAll = useCallback(
    (checks: PermissionInput[]) => {
      if (checks.length === 0) return true
      return checks.every((permission) => can(permission))
    },
    [can],
  )

  const canAny = useCallback(
    (checks: PermissionInput[]) => {
      if (checks.length === 0) return false
      return checks.some((permission) => can(permission))
    },
    [can],
  )

  const canAccess = useCallback(
    (permissionOrList: PermissionInput | PermissionInput[], options: { mode?: 'all' | 'any' } = { mode: 'all' }) => {
      const list = Array.isArray(permissionOrList) ? permissionOrList : [permissionOrList]
      const mode = options.mode ?? 'all'
      return mode === 'any' ? canAny(list) : canAll(list)
    },
    [canAll, canAny],
  )

  const enabledFeatures: EnabledFeatures = user?.enabled_features ?? {}
  const isFeatureEnabled = useCallback(
    (key: FeatureKey) => {
      if (!user) return false
      const val = enabledFeatures[key]
      if (val === undefined) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`useFeatureEnabled: unknown key '${key}' — defaulting to false`)
        }
        return false
      }
      return val
    },
    [user],
  )

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        loginWithToken,
        logout,
        isAuthenticated,
        isLoading,
        refreshUser,
        permissions,
        capabilities,
        hasRank,
        hasCap,
        can,
        canAccess,
        isFeatureEnabled,
        enabledFeatures,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    // Safe fallback for components rendered outside AuthProvider (e.g., external portal)
    return {
      user: null,
      token: null,
      login: async () => {},
      loginWithToken: async () => {},
      logout: () => {},
      isAuthenticated: false,
      isLoading: false,
      refreshUser: async () => {},
      permissions: [],
      capabilities: { ...EMPTY_CAPABILITIES },
      hasRank: () => false,
      hasCap: () => false,
      can: () => false,
      canAccess: () => false,
      isFeatureEnabled: () => false,
      enabledFeatures: {},
    }
  }
  return context
}
